import { Input, OnInit, ViewChild } from '@angular/core';
import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { channelStatePropertyMap, NotificationChannel, notificationChannels, NotificationChannelState, NotificationSendingMethod } from '../../../models/Notification';
import { INotificationTemplate, INotificationTemplateEdit } from '../../../models/NotificationTemplate';
import { AppService } from '../../../services/app.service';
import { ClassifierService } from '../../../services/classifier.service';
import { NotificationTemplateService } from '../../../services/notification-template.service';

@Component({
    selector: 'app-notification-template-edit',
    templateUrl: './notification-template-edit.component.html'
})
export class NotificationTemplateEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: NotificationTemplateService,
        private classifiers: ClassifierService
    ) { }

    @Input() data: INotificationTemplate;

    model: INotificationTemplateEdit = <INotificationTemplateEdit>{};
    name?: string;
    isNew: boolean;
    isFormSubmitted: boolean;

    readonly availableChannels: { [key: string]: boolean } = {};
    readonly requiredChannels: { [key: string]: boolean } = {};

    readonly options: {
        type: { value: string, text: string }[],
        channel: { value: NotificationChannel, text: string }[],
        sendingMethod: { value: NotificationSendingMethod, text: string, checked?: boolean }[]
    } = {
            type: [],
            channel: [],
            sendingMethod: []
        };

    readonly maxLengths = {
        name: 50,
        sender: 200,
        subject: 500,
        message: 4000,
        shortMessage: 1000
    };

    readonly textEditorConfig = {
        convert_urls: false,
        forced_root_block: '',
        entity_encoding: 'raw',
        plugins: 'link textcolor table code lists',
        toolbar:
            'undo redo | formatselect | bold italic forecolor backcolor'
            + ' | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat'
            + ' | table link | code'
    };

    get isShortContentRequired() {
        return this.availableChannels[NotificationChannel.Sms];
    }

    get isFullContentRequired() {
        return this.availableChannels[NotificationChannel.MyRsu] || this.availableChannels[NotificationChannel.RsuEmail];
    }

    get validations() {
        const values = this.model;

        const shortRequired = this.isShortContentRequired;
        const fullRequired = this.isFullContentRequired;

        return {
            name: {
                required: this.checkRequired(values.Name),
                pattern: /^[a-zA-Z0-9\-_]+$/.test(values.Name) == false && this.namePatternError,
                maxlength: this.checkMaxLength(values.Name, this.maxLengths.name)
            },
            type: {
                required: this.checkRequired(values.TypeId)
            },
            sender: {
                required: this.checkRequired(values.Sender),
                maxlength: this.checkMaxLength(values.Sender, this.maxLengths.sender)
            },
            sendingMethod: {
                required: this.checkRequired(values.SendingMethod)
            },
            subjectLV: {
                required: fullRequired && this.checkRequired(values.SubjectLV),
                maxlength: this.checkMaxLength(values.SubjectLV, this.maxLengths.subject)
            },
            subjectEN: {
                required: fullRequired && this.checkRequired(values.SubjectEN),
                maxlength: this.checkMaxLength(values.SubjectEN, this.maxLengths.subject)
            },
            messageLV: {
                required: fullRequired && this.checkRequired(values.MessageLV),
                maxlength: this.checkMaxLength(values.MessageLV, this.maxLengths.message)
            },
            messageEN: {
                required: fullRequired && this.checkRequired(values.MessageEN),
                maxlength: this.checkMaxLength(values.MessageEN, this.maxLengths.message)
            },
            shortMessageLV: {
                required: shortRequired && this.checkRequired(values.ShortMessageLV),
                maxlength: this.checkMaxLength(values.ShortMessageLV, this.maxLengths.shortMessage)
            },
            shortMessageEN: {
                required: shortRequired && this.checkRequired(values.ShortMessageEN),
                maxlength: this.checkMaxLength(values.ShortMessageEN, this.maxLengths.shortMessage)
            },
            availableChannels: {
                min: Object.values(this.availableChannels).filter(t => t).length == 0 && this.availableChannelRequiredError
            }
        };
    }

    readonly requiredError = this.app.translate('fieldIsRequired');
    readonly namePatternError = this.app.translate('notificationTemplate_hintName');
    readonly availableChannelRequiredError = this.app.translate('notificationTemplate_availableChannelRequired');

    @ViewChild('form', { static: false }) private form: NgForm;

    ngOnInit() {
        if (!this.data) return;

        this.options.channel = notificationChannels.map(t => {
            return {
                value: t,
                text: this.app.translate(`notification_channel${t}`)
            };
        });

        this.options.sendingMethod = [NotificationSendingMethod.Auto, NotificationSendingMethod.Manual].map(t => {
            return {
                value: t,
                text: this.app.translate(`notification_sendingMethod${t}`)
            };
        });

        this.app.addLoading(this.classifiers.get('NotificationType')).subscribe(data => {
            this.options.type = data.map(t => {
                return {
                    text: t.Value,
                    value: t.Id
                };
            });
        });

        this.isNew = !this.data.Id;

        this.fillModel();
    }

    onAvailableChannelChange(channel: NotificationChannel, checked: boolean) {
        this.availableChannels[channel] = checked;

        if (checked) {
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Available;
        } else {
            this.requiredChannels[channel] = false;
        }
    }

    onRequiredChannelChange(channel: NotificationChannel, checked: boolean) {
        if (checked) {
            this.availableChannels[channel] = true;
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Required;
        } else {
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Available;
        }
    }

    save() {
        this.isFormSubmitted = true;
        if (!this.validate()) return;

        const request = this.isNew ? this.service.create(this.model) : this.service.update(this.data.Id, this.model);

        this.app.addLoading(request).subscribe(() => {
            this.app.notify(this.app.translate('notificationTemplate_saved'));
            this.app.navigateByUrl('/notification-templates');
        });
    }

    getMaxLengthError(maxLength: number): string {
        return this.app.translate('fieldError_lengthMinMax').replace('{0}', 0).replace('{1}', maxLength);
    }

    getErrors(validation: { [key: string]: string }): string[] {
        return validation ? Object.values(validation).filter(t => !!t) : [];
    }

    private fillModel() {
        const data = this.data;

        this.name = data.Name;

        this.model.MessageEN = data.MessageEN;
        this.model.MessageLV = data.MessageLV;
        this.model.Name = data.Name;
        this.model.Sender = data.Sender;
        this.model.SendingMethod = data.SendingMethod as NotificationSendingMethod;
        this.model.ShortMessageEN = data.ShortMessageEN;
        this.model.ShortMessageLV = data.ShortMessageLV;
        this.model.SubjectEN = data.SubjectEN;
        this.model.SubjectLV = data.SubjectLV;
        this.model.TypeId = data.TypeId;
        this.model.MyRsuChannel = data.MyRsuChannel;
        this.model.RsuEmailChannel = data.RsuEmailChannel;
        this.model.SmsChannel = data.SmsChannel;

        notificationChannels.forEach(t => {
            const state = this.data[channelStatePropertyMap[t]];

            this.model[channelStatePropertyMap[t]] = state;

            this.availableChannels[t] = state >= NotificationChannelState.Available;
            this.requiredChannels[t] = state == NotificationChannelState.Required;
        });
    }

    private validate() {
        if (!this.form.valid || Object.values(this.validations).some(t => Object.values(t).includes(true))) {
            this.app.alert.error(this.app.translate('formError'));
            return false;
        }

        return true;
    }

    private checkRequired(value: any): string {
        return (value === undefined || value === null || value === '') ? this.requiredError : undefined;
    }

    private checkMaxLength(value: string, maxLength: number): string {
        return value && value.length > maxLength ? this.getMaxLengthError(maxLength) : undefined;
    }
}
